import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { DataTable } from '@/components/ui/table/data-table';
import useBrains from '@/hooks/useBrains';
import useDialogs from '@/hooks/useDialogs';
import { selectAccountSlug } from '@/redux/session/selectors';
import { Webhook } from '@/shared/types/webhook';

import { createColumns } from './columns';
import { EVENT_WEBHOOK_TYPES } from './WebhookStatusDot';

interface WebhooksTableProps {
  webhooks: Webhook[];
  brainId: string;
  canWrite: boolean;
  isLoading?: boolean;
  className?: string;
}

export function WebhooksTable({
  webhooks,
  brainId,
  canWrite,
  isLoading = false,
  className,
}: WebhooksTableProps) {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const { dialogsByWebhookId } = useDialogs(brainId);
  const { isWebhookUsedInAuth } = useBrains(brainId);

  // Event webhooks show enabled/disabled instead, so skip the (unused) walk for them.
  const usedWebhookIds = useMemo(() => {
    const used = new Set<string>();
    for (const w of webhooks) {
      if (EVENT_WEBHOOK_TYPES.includes(w.type)) {
        continue;
      }
      const isUsed = dialogsByWebhookId.has(w.webhook_id) || isWebhookUsedInAuth(w.webhook_id);
      if (isUsed) {
        used.add(w.webhook_id);
      }
    }
    return used;
  }, [webhooks, dialogsByWebhookId, isWebhookUsedInAuth]);

  const columns = useMemo(
    () => createColumns({ brainId, slug, canWrite, usedWebhookIds, dialogsByWebhookId }),
    [brainId, slug, canWrite, usedWebhookIds, dialogsByWebhookId]
  );

  // Pin event webhooks to top in fixed order (first → pre → post)
  const rowPinning = useMemo(() => {
    const eventTypeOrder: Record<string, number> = {
      first_message: 1,
      pre_message: 2,
      post_message: 3,
    };

    const eventWebhooks = webhooks
      .filter((w) => w.type !== 'action')
      .sort((a, b) => (eventTypeOrder[a.type] || 999) - (eventTypeOrder[b.type] || 999));

    return { top: eventWebhooks.map((w) => w.webhook_id), bottom: [] };
  }, [webhooks]);

  const getRowHref = (webhook: Webhook) =>
    `/${slug}/ai-agents/${brainId}/workflows/webhooks/${webhook.webhook_id}`;

  return (
    <DataTable.Root
      fill
      columns={columns}
      data={webhooks}
      rowCount={webhooks.length}
      isLoading={isLoading}
      getRowHref={getRowHref}
      resourceName="webhook"
      enableRowHover
      className={className}
      emptyStateMessage={t('webhook.no_search_results')}
      autoResetPageIndex
      enableRowPinning={true}
      keepPinnedRows={true}
      rowPinning={rowPinning}
      getRowId={(row) => row.webhook_id}
    >
      <DataTable.Content />
      <DataTable.Pagination />
    </DataTable.Root>
  );
}
